import type { AsyncDataRequestStatus } from "nuxt/dist/app/composables/asyncData";
import type { ReactiveString, RequestOptions } from "../types/request";
import type { XhrResponse, ApiResponse } from "../types/response";

export async function useTengivaApi<T>(
	endpoint: ReactiveString,
	options: RequestOptions
) {
	const { apiUrl, platformName } = useRuntimeConfig().public;

	// Set the base request URL if not set
	if (!options?.baseURL) {
		const serviceName = options?.service?.name || "BE";
		const serviceVersion = options?.service?.version
			? `v${options.service.version}`
			: `v1`;
		options = {
			...options,
			baseURL: `${apiUrl}/${serviceVersion}/${serviceName}`,
		};
	}

	// Append Platform ID to Tengiva Platform Header
	let platformHeader = platformName;
	if (options?.platform_id) {
		platformHeader += " " + options.platform_id;
	}

	// Add Plaftorm & Auth headers
	const additionalHeaders: any = {
		"Tengiva-Platform": platformHeader,
	};
	const token = getActiveIdentityToken();
	if (token) {
		additionalHeaders.Authorization = `Bearer ${token}`;
	}
	options.headers = Object.assign(options.headers || {}, additionalHeaders);

	if (options?.requestFunc === "xhr") {
		// Ref: https://github.com/unjs/ofetch/issues/45
		// When ofetch supports upload progress track, xhr can be removed
		const { data, pending, status, error } = await useXHR(
			endpoint as string,
			options
		);

		// @ts-ignore
		return {
			response: computed((): ApiResponse<T> => data.value),
			pending,
			status,
			error: computed(() => error.value),
		};
	} else if (options?.requestFunc === "$fetch") {
		const { baseURL, headers, body, method } = options;
		const $fetchOptions: any = {
			headers,
			body,
			method,
			baseURL,
		};
		const rawResponse = await $fetch<{
			response: ApiResponse<T>;
			error: boolean | { message: string; [key: string]: any };
		}>(endpoint as string, $fetchOptions);

		// @ts-ignore
		return {
			response: computed((): ApiResponse<T> => rawResponse.response),
			error: computed(() =>
				typeof rawResponse.error === "boolean"
					? null
					: Error(rawResponse.error.message)
			),
			blob: rawResponse instanceof Blob ? rawResponse : null,
		};
	} else {
		const { data, pending, status, execute, refresh, error } = await useFetch(
			endpoint,
			options
		);
		// @ts-ignore
		return {
			response: computed((): ApiResponse<T> => data.value?.response),
			pending,
			status,
			execute,
			refresh,
			error: computed(() => {
				if (error.value) return error.value;
				else {
					return typeof data.value?.error === "boolean"
						? null
						: data.value?.error;
				}
			}),
			blob: data.value instanceof Blob ? data.value : null,
		};
	}
}

const useXHR = <T = any>(
	url: string,
	options: RequestOptions
): Promise<XhrResponse<T>> => {
	if (process.server)
		return new Promise(resolve =>
			resolve({
				data: ref<any>(null),
				pending: ref(true),
				status: ref<AsyncDataRequestStatus>("idle"),
				error: ref<Error | null>(null),
			})
		);

	return new Promise((resolve, reject) => {
		const { body, responseType } = options;
		const data = ref<any>({ data: null, error: null });
		const error = ref<Error | null>(null);
		const status = ref<AsyncDataRequestStatus>("idle");
		const pending = ref(true);

		const request: XMLHttpRequest = createXhrInstance(
			`${options.baseURL}/${url}`,
			options
		);

		if (responseType) {
			// @ts-ignore
			request.responseType = responseType?.value || responseType;
		}
		request.onloadstart = function loadStart() {
			if (options.onInstanceReady) {
				options.onInstanceReady(request);
			}
		};
		request.onreadystatechange = function statusChange() {
			if (request.readyState < 4) {
				status.value = "pending";
			} else if (request.status >= 200 && request.status < 400) {
				let rawResponse = null;
				if (request.responseText) {
					rawResponse = JSON.parse(request.responseText);
					data.value = rawResponse.response;
					status.value = "success";
					pending.value = false;
					resolve({ data, pending, error, status });
				}
			} else {
				error.value = Error(request.responseText);
				status.value = "error";
				pending.value = false;
				reject({ data, pending, error, status });
			}
		};

		request.onabort = function handleAbort() {
			error.value = Error("Aborted");
			pending.value = false;
			status.value = "idle";
			reject({ data, pending, error, status });
		};

		request.upload.onprogress = function handleProgress(e) {
			if (e.lengthComputable && options.onUploadProgress) {
				options.onUploadProgress(e);
			}
		};

		request.onerror = function handleError() {
			error.value = Error(request.responseText);
			pending.value = false;
			status.value = "error";
			reject({ data, pending, error, status });
		};

		request.send(body as XMLHttpRequestBodyInit | Document);
	});
};

const createXhrInstance = (url: string, options: any) => {
	const { headers, method } = options;
	const request = new XMLHttpRequest();
	request.open(method.toUpperCase(), url as string, true);
	Object.keys(headers).forEach(name => {
		request.setRequestHeader(name, headers[name]);
	});

	return request;
};

/**
 * Get the current Identity token from the Cookies
 *
 * Will only return it if it isn't expired
 * @returns string|false
 */
const getActiveIdentityToken = () => {
	const jwtToken = useCookie<string>("jwt_token");

	if (!jwtToken.value) {
		return false;
	}

	if (isTokenExpired(jwtToken.value)) {
		return false;
	}

	return jwtToken.value;
};

/**
 * Check if a JWT is expired
 * @param token
 * @returns
 */
const isTokenExpired = (token: string) => {
	const jwtObject = parseJWT(token);

	const expInMilliseconds = jwtObject.exp * 1000;

	const expiry = new Date(expInMilliseconds);

	return expiry < new Date();
};
